import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import {
  ChevronLeft,
  Trophy,
  BarChart3,
} from "lucide-react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  where,
} from "firebase/firestore";
import { auth, db } from "../../../firebase";
import { getMovementBySlug } from "../../../lib/training";
import PBShareModal from "../../training/components/PBShareModal";
import MovementHistorySection from "../components/MovementHistorySection";
import MovementLogForm from "../components/MovementLogForm";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceDot,
} from "recharts";
import UserTopNav from "../../../components/layout/UserTopNav";
import {
  formatChartValue,
  formatDisplayValue,
  getAccentClasses,
  getSmartFormConfig,
  isBetterPerformance,
  isTimeDisplay,
  parseChartValue,
  prettyDate,
  shortDate,
  type FormFieldErrors,
  validateTrainingLogForm,
} from "../utils/movementHelpers";
import { getDateInputValueInTimeZone } from "../../../utils/date";
import { gymConfig } from "../../../config/gymConfig";

type TrainingLog = {
  id: string;
  category: string;
  movementSlug: string;
  movementName: string;
  metricType: string;
  value: string;
  unit: string;
  reps?: string;
  date: string;
  notes: string;
};

type ChartPoint = {
  id: string;
  date: string;
  shortDate: string;
  value: number;
  metricType: string;
  isBest: boolean;
};

function CustomTooltip({
  active,
  payload,
  label,
  categoryKey,
  movementName,
  unit,
}: {
  active?: boolean;
  payload?: Array<{ payload: ChartPoint }>;
  label?: string;
  categoryKey?: string | null;
  movementName?: string;
  unit?: string;
}) {
  if (!active || !payload?.length) return null;

  const point = payload[0].payload;

  return (
    <div className="rounded-xl border border-white/10 bg-neutral-950/95 px-3.5 py-3 shadow-[0_16px_40px_rgba(0,0,0,0.28)] backdrop-blur">
      <div className="text-[11px] font-medium tracking-[0.02em] text-white/45">
        {label}
      </div>
      <div className="mt-2 text-lg font-semibold text-white">
        {formatChartValue(point.value, categoryKey, movementName, unit)}
      </div>
      <div className="mt-1 text-sm text-white/55">{point.metricType}</div>
    </div>
  );
}

export default function TrainingStandardMovement() {
  const timeZone = "Europe/London";
  const { category, movementSlug } = useParams<{
    category: string;
    movementSlug: string;
  }>();

  const result = getMovementBySlug(category, movementSlug);

  const selectedCategory = result?.category ?? null;
  const movement = result?.movement ?? null;

  const accent = getAccentClasses(selectedCategory?.key);

  const defaultMetricType = movement?.metricTypes[0] ?? "Entry";
  const defaultUnit = movement?.unitOptions[0] ?? "";

  const [logs, setLogs] = useState<TrainingLog[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [saved, setSaved] = useState(false);
  const [metricType, setMetricType] = useState(defaultMetricType);
  const [value, setValue] = useState("");
  const [unit, setUnit] = useState(defaultUnit);
  const [reps, setReps] = useState("");
  const [date, setDate] = useState(() =>
    getDateInputValueInTimeZone(new Date(), timeZone)
  );
  const [notes, setNotes] = useState("");
  const [formErrors, setFormErrors] = useState<FormFieldErrors>({});
  const [saveError, setSaveError] = useState("");
  const [activeMetricFilter, setActiveMetricFilter] = useState(
    movement?.metricTypes[0] ?? ""
    );

  const [shareOpen, setShareOpen] = useState(false);
  const [isNewPB, setIsNewPB] = useState(false);
  const [deletingLogId, setDeletingLogId] = useState<string | null>(null);
  const [sharePayload, setSharePayload] = useState<{
    movement: string;
    metricType: string;
    value: string;
    unit?: string;
    dateLabel?: string;
  } | null>(null);

const movementLogs = useMemo(() => {
  if (!selectedCategory || !movement) return [];
  return [...logs].sort((a, b) => b.date.localeCompare(a.date));
}, [logs, selectedCategory, movement]);

const metricFilterOptions = useMemo(() => {
  return movement?.metricTypes ?? [];
}, [movement?.metricTypes]);

const filteredLogs = useMemo(() => {
  if (!activeMetricFilter) return [];
  return movementLogs.filter((log) => log.metricType === activeMetricFilter);
}, [movementLogs, activeMetricFilter]);

  const bestLog = useMemo(() => {
  if (!selectedCategory) return null;

  const numericLogs = filteredLogs
    .map((log) => ({
      ...log,
      parsed: parseChartValue(log.value, log.unit),
    }))
        .filter((log) => log.parsed !== null) as Array<
        TrainingLog & { parsed: number }
    >;

    if (!numericLogs.length) return null;

    if (selectedCategory.key === "engine") {
        return numericLogs.reduce((best, current) =>
        current.parsed < best.parsed ? current : best
        );
    }

    return numericLogs.reduce((best, current) =>
        current.parsed > best.parsed ? current : best
    );
    }, [filteredLogs, selectedCategory]);

  const latestLog = filteredLogs[0] ?? null;

  const chartData = useMemo<ChartPoint[]>(() => {
  const numeric = filteredLogs
    .map((log) => {
      const parsed = parseChartValue(log.value, log.unit);
      if (parsed === null) return null;

      return {
        id: log.id,
        date: prettyDate(log.date),
        shortDate: shortDate(log.date),
        value: parsed,
        metricType: log.metricType,
        isBest: bestLog?.id === log.id,
      };
    })
    .filter(Boolean) as ChartPoint[];

  return [...numeric].reverse();
}, [filteredLogs, bestLog]);

const formConfig = useMemo(() => {
  return getSmartFormConfig({
    categoryKey: selectedCategory?.key,
    movementName: movement?.name,
    movementSlug: movement?.slug,
    metricType,
    unitOptions: movement?.unitOptions ?? [],
  });
}, [
  selectedCategory?.key,
  movement?.name,
  movement?.slug,
  movement?.unitOptions,
  metricType,
]);

const effectiveUnit = formConfig.lockedUnit ?? unit;

  const chartMin = useMemo(() => {
    if (!chartData.length) return 0;
    const min = Math.min(...chartData.map((point) => point.value));
    return Math.max(0, min - Math.max(2, Math.round(min * 0.05)));
  }, [chartData]);

  const chartMax = useMemo(() => {
    if (!chartData.length) return 100;
    const max = Math.max(...chartData.map((point) => point.value));
    return max + Math.max(2, Math.round(max * 0.05));
  }, [chartData]);

    function resetForm() {
    const nextMetricType = movement?.metricTypes[0] ?? "Entry";

    setMetricType(nextMetricType);
    setValue("");

    const nextConfig = getSmartFormConfig({
        categoryKey: selectedCategory?.key,
        movementName: movement?.name,
        movementSlug: movement?.slug,
        metricType: nextMetricType,
        unitOptions: movement?.unitOptions ?? [],
    });

    setUnit(nextConfig.lockedUnit ?? movement?.unitOptions[0] ?? "");
    setReps("");
    setDate(getDateInputValueInTimeZone(new Date(), timeZone));
    setNotes("");
    setFormErrors({});
    setSaveError("");
    }

  useEffect(() => {
  const nextMetricType = movement?.metricTypes[0] ?? "Entry";

  setMetricType(nextMetricType);
  setValue("");

  const nextConfig = getSmartFormConfig({
    categoryKey: selectedCategory?.key,
    movementName: movement?.name,
    movementSlug: movement?.slug,
    metricType: nextMetricType,
    unitOptions: movement?.unitOptions ?? [],
  });

  setUnit(nextConfig.lockedUnit ?? movement?.unitOptions[0] ?? "");
  setReps("");
  setDate(getDateInputValueInTimeZone(new Date(), timeZone));
  setNotes("");
  setSaved(false);
  setFormErrors({});
  setSaveError("");
  setActiveMetricFilter(movement?.metricTypes[0] ?? "");
}, [
  selectedCategory?.key,
  movement?.slug,
  movement?.name,
  movement?.metricTypes,
  movement?.unitOptions,
]);

  useEffect(() => {
    if (!saved) return;

    const timer = window.setTimeout(() => setSaved(false), 2200);
    return () => window.clearTimeout(timer);
  }, [saved]);

  useEffect(() => {
    if (!selectedCategory || !movement) {
      setLogs([]);
      return;
    }

    const user = auth.currentUser;

    if (!user) {
      setLogs([]);
      setLoadError("You need to be signed in to view training logs.");
      return;
    }

    setLoadError("");

    const logsRef = collection(db, "users", user.uid, "trainingLogs");
    const q = query(
      logsRef,
      where("category", "==", selectedCategory.key),
      where("movementSlug", "==", movement.slug),
      orderBy("date", "desc")
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const nextLogs: TrainingLog[] = snapshot.docs.map((doc) => {
          const data = doc.data();

          return {
            id: doc.id,
            category: data.category ?? "",
            movementSlug: data.movementSlug ?? "",
            movementName: data.movementName ?? "",
            metricType: data.metricType ?? "",
            value: data.value ?? "",
            unit: data.unit ?? "",
            reps: data.reps ?? "",
            date: data.date ?? "",
            notes: data.notes ?? "",
          };
        });

        setLogs(nextLogs);
      },
      (error) => {
        console.error("Error loading training logs:", error);
        setLoadError(error.message || "Could not load training logs.");
      }
    );

    return () => unsubscribe();
  }, [selectedCategory, movement]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    const user = auth.currentUser;

    if (!user || !selectedCategory || !movement) return;

    const nextErrors = validateTrainingLogForm({
      value,
      reps,
      date,
      notes,
      unit: effectiveUnit,
      categoryKey: selectedCategory.key,
      movementName: movement.name,
      showRepsField: formConfig.showRepsField,
    });

    setFormErrors(nextErrors);
    setSaveError("");

    if (Object.keys(nextErrors).length > 0) {
      setSaved(false);
      return;
    }

    try {
      setIsSaving(true);
      setSaved(false);
      setIsNewPB(false);

      const submittedValue = value.trim();
      const submittedUnit = effectiveUnit;
      const parsedSubmittedValue = parseChartValue(submittedValue, submittedUnit);

      const currentBestParsed =
        bestLog ? parseChartValue(bestLog.value, bestLog.unit) : null;

      const nextIsPB =
        parsedSubmittedValue !== null &&
        isBetterPerformance({
          categoryKey: selectedCategory.key,
          nextValue: parsedSubmittedValue,
          currentBestValue: currentBestParsed,
        });

      const logsRef = collection(db, "users", user.uid, "trainingLogs");

      await addDoc(logsRef, {
        userId: user.uid,
        category: selectedCategory.key,
        movementSlug: movement.slug,
        movementName: movement.name,
        metricType,
        value: submittedValue,
        unit: submittedUnit,
        reps: reps.trim(),
        date,
        notes: notes.trim(),
        createdAt: serverTimestamp(),
      });

      setIsNewPB(Boolean(nextIsPB));

      if (nextIsPB) {
        setSharePayload({
          movement: movement.name,
          metricType,
          value: submittedValue,
          unit: submittedUnit,
          dateLabel: prettyDate(date),
        });
      } else {
        setSharePayload(null);
      }

      resetForm();
      setSaved(true);
    } catch (error) {
      console.error("Error saving training log:", error);
      setSaveError("Could not save this log. Please try again.");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDeleteLog(logId: string) {
    const user = auth.currentUser;

    if (!user) return;

    const confirmed = window.confirm(
      "Delete this metric entry? This can’t be undone."
    );

    if (!confirmed) return;

    try {
      setDeletingLogId(logId);
      setSaveError("");

      await deleteDoc(doc(db, "users", user.uid, "trainingLogs", logId));

      if (sharePayload && filteredLogs.some((log) => log.id === logId)) {
        setShareOpen(false);
        setSharePayload((current) => {
          if (!current) return null;
          const deletedLog = filteredLogs.find((log) => log.id === logId);
          if (!deletedLog) return current;

          return current.value === deletedLog.value &&
            current.metricType === deletedLog.metricType &&
            current.dateLabel === prettyDate(deletedLog.date)
            ? null
            : current;
        });
      }
    } catch (error) {
      console.error("Error deleting training log:", error);
      setSaveError("Could not delete this log. Please try again.");
    } finally {
      setDeletingLogId(null);
    }
  }

  if (!selectedCategory || !movement) {
    return <Navigate to="/training" replace />;
  }

  const Icon = selectedCategory.icon;

  return (
    <div className="app-page min-h-screen text-white">
        <UserTopNav />
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-4 px-4 py-5 sm:px-6 lg:px-8">
        <section className="relative overflow-hidden rounded-[28px] border border-white/10 bg-[linear-gradient(180deg,rgba(14,14,16,0.98),rgba(8,8,10,0.98))] shadow-[0_22px_60px_rgba(0,0,0,0.34)]">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(79,124,255,0.12),transparent_24%),radial-gradient(circle_at_82%_18%,rgba(121,162,255,0.10),transparent_24%),linear-gradient(180deg,rgba(255,255,255,0.02),transparent_48%)]" />
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:34px_34px] opacity-[0.04]" />

          <div className="relative p-5 sm:p-6 lg:p-7">
            <div className="grid gap-6 lg:grid-cols-[minmax(0,1.35fr)_minmax(220px,0.75fr)] lg:items-end">
              <div className="max-w-3xl">
                <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 text-[11px] font-medium text-white/65 backdrop-blur-md">
                  <Icon className="h-3.5 w-3.5" />
                  {selectedCategory.label}
                </div>

                <h1 className="text-3xl font-semibold tracking-[-0.04em] text-white sm:text-4xl lg:text-[2.9rem]">
                  {movement.name}
                </h1>

                <p className="mt-3 max-w-2xl text-sm leading-6 text-white/70 sm:text-[15px]">
                  {movement.description}
                </p>

                <div className="mt-4 flex flex-wrap items-center gap-2 text-[11px] text-white/52">
                  <span className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5">
                    {gymConfig.training.benchmarkLabel}
                  </span>
                  <span className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5">
                    {activeMetricFilter || metricType}
                  </span>
                  <span className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5">
                    {filteredLogs.length} {filteredLogs.length === 1 ? "log" : "logs"}
                  </span>
                </div>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row lg:flex-col lg:items-end">
                <div className="rounded-[18px] border border-white/10 bg-white/[0.03] px-4 py-3.5 backdrop-blur">
                  <div className="text-[11px] font-medium text-white/45">
                    Current best
                  </div>
                  <div className="mt-1.5 text-lg font-semibold tracking-[-0.03em] text-white">
                    {bestLog
                      ? formatDisplayValue(
                          bestLog.value,
                          bestLog.unit,
                          selectedCategory.key,
                          movement.name
                        )
                      : "—"}
                  </div>
                </div>

                <div className="flex flex-wrap gap-3 lg:justify-end">
                  <Link
                    to="/training"
                    className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2.5 text-sm font-medium text-white/78 backdrop-blur-md transition hover:border-white/20 hover:bg-white/[0.06] hover:text-white"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Training
                  </Link>

                  <Link
                    to={`/training/${selectedCategory.key}`}
                    className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2.5 text-sm font-medium text-white/78 backdrop-blur-md transition hover:border-white/20 hover:bg-white/[0.06] hover:text-white"
                  >
                    {selectedCategory.label}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="grid gap-3 md:grid-cols-3">
          <div className="rounded-[18px] border border-white/10 bg-white/[0.025] px-4 py-3.5">
            <div className="text-[11px] font-medium text-white/45">
              {selectedCategory.key === "engine" ? "Fastest" : "Best"} · {activeMetricFilter}
            </div>
            <div className="mt-2 flex items-center gap-3">
              <div className={`rounded-2xl border p-2.5 ${accent.badgeGlow}`}>
                <Trophy className="h-4 w-4" />
              </div>
              <div className="text-2xl font-semibold tracking-[-0.04em]">
                {bestLog
                  ? formatDisplayValue(
                      bestLog.value,
                      bestLog.unit,
                      selectedCategory.key,
                      movement.name
                    )
                  : "—"}
              </div>
            </div>
            <div className="mt-2 text-sm text-white/55">
              {bestLog ? `${bestLog.metricType} · ${prettyDate(bestLog.date)}` : "No best entry yet"}
            </div>
          </div>

          <div className="rounded-[18px] border border-white/10 bg-white/[0.025] px-4 py-3.5">
            <div className="text-[11px] font-medium text-white/45">
              Latest entry
            </div>
            <div className="mt-2 text-2xl font-semibold tracking-[-0.04em]">
              {latestLog
                ? formatDisplayValue(
                    latestLog.value,
                    latestLog.unit,
                    selectedCategory.key,
                    movement.name
                  )
                : "—"}
            </div>
            <div className="mt-2 text-sm text-white/55">
              {latestLog ? `${latestLog.metricType} · ${prettyDate(latestLog.date)}` : "No logs yet"}
            </div>
          </div>

          <div className="rounded-[18px] border border-white/10 bg-white/[0.025] px-4 py-3.5">
            <div className="text-[11px] font-medium text-white/45">
              Total logs
            </div>
            <div className="mt-2 text-2xl font-semibold tracking-[-0.04em]">
              {filteredLogs.length}
            </div>
            <div className="mt-2 text-sm text-white/55">
              Entries recorded for {movement.name}
            </div>
          </div>
        </section>

        <section className="relative overflow-hidden rounded-[22px] border border-white/10 bg-[linear-gradient(180deg,rgba(15,15,17,0.96),rgba(9,9,10,0.96))] p-4 sm:p-5">
          <div className={`absolute inset-0 ${accent.glow}`} />
          <div className={`absolute inset-x-0 top-0 h-px opacity-70 ${accent.line}`} />

          <div className="relative">
            <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 text-[11px] font-medium text-white/50">
                    <BarChart3 className="h-3.5 w-3.5" />
                    Progress
                    </div>
                    <h2 className="mt-3 text-xl font-semibold tracking-[-0.03em] text-white sm:text-2xl">
                    Progress over time
                    </h2>
                    <p className="mt-2 max-w-2xl text-sm leading-6 text-white/62">
                    Track how this benchmark moves across sessions and spot PB moments faster.
                    </p>
                    <p className="mt-1.5 text-sm text-white/50">
                    {activeMetricFilter
                        ? `Viewing ${activeMetricFilter} entries.`
                        : "Select a metric type to view progress."}
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {metricFilterOptions.map((option) => {
                        const isActive = activeMetricFilter === option;

                        return (
                          <button
                            key={option}
                            type="button"
                            onClick={() => {
                              setActiveMetricFilter(option);
                              setMetricType(option);
                            }}
                            className={[
                              "rounded-full border px-3.5 py-2 text-sm font-medium transition",
                              isActive
                                ? `${accent.badgeGlow}`
                                : "border-white/10 bg-white/[0.03] text-white/65 hover:border-white/15 hover:bg-white/[0.05] hover:text-white",
                            ].join(" ")}
                          >
                            {option}
                          </button>
                        );
                      })}
                    </div>
                </div>

                <div className="rounded-[16px] border border-white/10 bg-white/[0.03] px-4 py-3.5 text-sm text-white/62 backdrop-blur">
                    <div className="text-[11px] font-medium text-white/45">
                    {selectedCategory.key === "engine" ? "Current fastest" : "Current best"} · {activeMetricFilter}
                    </div>
                    <div className="mt-3 text-sm text-white/62">
                    {bestLog
                        ? `${bestLog.metricType} · ${prettyDate(bestLog.date)}`
                        : selectedCategory.key === "engine"
                        ? `No ${activeMetricFilter} time yet`
                        : `No ${activeMetricFilter} entry yet`}
                    </div>
                </div>
                </div>

            {chartData.length >= 2 ? (
              <div className="h-[260px] w-full rounded-[20px] border border-white/10 bg-black/20 p-3 sm:h-[280px] sm:p-4">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={chartData}
                    margin={{ top: 16, right: 12, left: 0, bottom: 8 }}
                  >
                    <CartesianGrid stroke={accent.chartGrid} strokeDasharray="3 3" />
                    <XAxis
                      dataKey="shortDate"
                      stroke="rgba(255,255,255,0.38)"
                      tickLine={false}
                      axisLine={false}
                      fontSize={12}
                    />
                    <YAxis
                        stroke="rgba(255,255,255,0.38)"
                        tickLine={false}
                        axisLine={false}
                        fontSize={12}
                        domain={[chartMin, chartMax]}
                        allowDecimals={selectedCategory.key === "personal"}
                        tickFormatter={(val) =>
                            formatChartValue(Number(val), selectedCategory.key, movement.name, effectiveUnit)
                    }
                    />
                    <Tooltip
                    content={
                        <CustomTooltip
                        categoryKey={selectedCategory.key}
                        movementName={movement.name}
                        unit={effectiveUnit}
                        />
                    }
                    />
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke={accent.chartStroke}
                      strokeWidth={3}
                      dot={{
                        r: 4,
                        fill: accent.chartDot,
                        stroke: accent.chartStroke,
                        strokeWidth: 2,
                      }}
                      activeDot={{
                        r: 6,
                        fill: accent.chartDot,
                        stroke: accent.chartStroke,
                        strokeWidth: 2,
                      }}
                    />
                    {chartData
                      .filter((point) => point.isBest)
                      .map((point) => (
                        <ReferenceDot
                          key={point.id}
                          x={point.shortDate}
                          y={point.value}
                          r={7}
                          fill={accent.chartDot}
                          stroke={accent.chartStroke}
                          strokeWidth={3}
                        />
                      ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="rounded-[20px] border border-dashed border-white/10 bg-black/20 p-5 text-sm text-white/55">
                Add at least two numeric logs to unlock the movement progress chart.
              </div>
            )}
          </div>
        </section>

        <div className="grid gap-6 xl:grid-cols-[1.02fr_0.98fr]">
          <MovementLogForm
            movementName={movement.name}
            metricTypes={movement.metricTypes}
            unitOptions={movement.unitOptions}
            metricType={metricType}
            setMetricType={setMetricType}
            unit={unit}
            setUnit={setUnit}
            effectiveUnit={effectiveUnit}
            value={value}
            setValue={setValue}
            reps={reps}
            setReps={setReps}
            date={date}
            setDate={setDate}
            notes={notes}
            setNotes={setNotes}
            formConfig={formConfig}
            formErrors={formErrors}
            clearFieldError={(field) =>
              setFormErrors((current) => ({ ...current, [field]: undefined }))
            }
            handleSubmit={handleSubmit}
            isSaving={isSaving}
            saved={saved}
            isNewPB={isNewPB}
            hasSharePayload={Boolean(sharePayload)}
            onShare={() => setShareOpen(true)}
            saveError={saveError}
            loadError={loadError}
            accent={accent}
          />

          <MovementHistorySection
            accent={accent}
            movementName={movement.name}
            activeMetricFilter={activeMetricFilter}
            filteredLogs={filteredLogs}
            bestLogId={bestLog?.id}
            deletingLogId={deletingLogId}
            isTimeDisplay={(unitValue, movementNameValue) =>
              isTimeDisplay(unitValue, selectedCategory.key, movementNameValue)
            }
            formatDisplayValue={(rawValue, unitValue, movementNameValue) =>
              formatDisplayValue(rawValue, unitValue, selectedCategory.key, movementNameValue)
            }
            prettyDate={prettyDate}
            onShareLog={(log) => {
              setSharePayload({
                movement: movement.name,
                metricType: log.metricType,
                value: log.value,
                unit: log.unit,
                dateLabel: prettyDate(log.date),
              });
              setShareOpen(true);
            }}
            onDeleteLog={handleDeleteLog}
          />
        </div>
        <PBShareModal
          open={shareOpen && !!sharePayload}
          onClose={() => setShareOpen(false)}
          athleteName={auth.currentUser?.displayName || gymConfig.brand.athleteLabel}
          movement={sharePayload?.movement || movement.name}
          metricType={sharePayload?.metricType || metricType}
          value={sharePayload?.value || value}
          unit={sharePayload?.unit || effectiveUnit}
          dateLabel={sharePayload?.dateLabel || prettyDate(date)}
          categoryKey={selectedCategory?.key}
        />
      </div>
    </div>
  );
}
